import { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { createReducerContext } from "react-use";

import { useToast } from "@smartrent/ui";

import { StateType, useAppState } from "@/context/AppState";
import { VerificationApiType, VerificationReducerActions } from "@/types";
import {
  checkForDocumentFailures,
  initialState,
  parseAttempts,
  verificationReducer,
} from "@/utils/verification";

import { authenticatedRequest } from "./api";
import { useProspectVerificationId } from "./tour-feedback";

const [useVerificationReducer, VerificationProvider] = createReducerContext(
  verificationReducer,
  initialState
);

export function useVerification({
  accessToken,
  groupTourConfigUuid,
}: {
  accessToken: string;
  groupTourConfigUuid: string;
}) {
  const [state, dispatch] = useVerificationReducer();
  const [{ tour, config }, setAppState] = useAppState();
  const [verificationId] = useProspectVerificationId();
  const setToast = useToast();
  const history = useHistory();

  const checkVerification = useCallback(
    ({
      groupTourConfigUuid,
      verificationId,
      accessToken,
    }: {
      groupTourConfigUuid: string;
      verificationId?: string;
      accessToken: string;
    }) => {
      return authenticatedRequest({
        method: "GET",
        url: "/identity-verification",
        accessToken,
        params: verificationId
          ? {
              prospect_verification_id: verificationId,
              group_uuid: groupTourConfigUuid,
            }
          : { group_uuid: groupTourConfigUuid },
      })
        .then(({ data }: { data: VerificationApiType }) => {
          if (!data) return null;
          if (data.verified_at || data.manually_verified_at) {
            return dispatch({
              type: VerificationReducerActions.already_verified,
            });
          } else if (
            tour?.identityVerificationResult === "pending_manual_verification"
          ) {
            return dispatch({
              type: VerificationReducerActions.needs_manual_verification,
            });
          }
          const { front, back, selfie } = parseAttempts(data.attempts_images);
          dispatch({
            type: VerificationReducerActions.update_attempts,
            payload: { front, back, selfie },
          });
          switch (
            checkForDocumentFailures({
              attemptsImages: data.attempts_images,
              documentType: data.document_type,
            })
          ) {
            case "documents_failed":
              return dispatch({
                type: VerificationReducerActions.documents_failed,
              });
            case "documents_have_failure":
              return dispatch({
                type: VerificationReducerActions.documents_have_failure,
              });
            case "selfie_has_failure":
              return dispatch({
                type: VerificationReducerActions.selfie_has_failure,
              });
            default:
              break;
          }
          if (
            (data.document_type === "passport" && data.front_path) ||
            (data.front_path && data.back_path)
          ) {
            return dispatch({
              type: VerificationReducerActions.documents_completed,
            });
          }
        })
        .catch(() => {
          return null;
        })
        .finally(() => {
          dispatch({ type: VerificationReducerActions.post_verification });
        });
    },
    [dispatch, tour?.identityVerificationResult]
  );

  const finalizeTour = useCallback(
    ({
      tourToken,
      accessToken,
    }: {
      tourToken: string;
      accessToken: string;
    }) => {
      if (
        (tour?.scheduledStartNow || tour?.scheduledStart) &&
        tour.units.length == 0
      ) {
        history.push(
          `/tour/${tour?.token}/units?tour_now=${tour.scheduledStartNow}&start_time=${tour.scheduledStart}&end_time=${tour.scheduledEnd}`
        );
        return;
      } else {
        dispatch({ type: VerificationReducerActions.start_finalize_tour });

        return authenticatedRequest({
          method: "POST",
          url: `/tour/${tourToken}/finalize`,
          accessToken,
        })
          .then(() => {
            dispatch({
              type: VerificationReducerActions.finalize_tour_success,
            });
          })
          .catch(async (error) => {
            let message: string | null = null;
            let unitNotTourable = false;
            let tourAlreadyFinalized = false;

            error?.response?.data?.errors?.forEach(
              (error: any, _index: number) => {
                message =
                  error?.message || message || "An unknown error occurred";
                if (error.code === "one_or_more_units_are_not_tourable")
                  unitNotTourable = true;

                if (error.code === "tour_already_finalized")
                  tourAlreadyFinalized = true;
              }
            );

            if (message) {
              setToast({
                title: "Error",
                message: message,
                status: "error",
              });
            }
            // Redirect the user back to the schedule page if the tour fails to finalize
            // It is probably because the timeslot was taken already
            if (unitNotTourable) {
              setAppState((state: StateType) => ({
                ...state,
                tour: undefined,
                prospect: undefined,
              }));
              await dispatch({ type: VerificationReducerActions.reset_state });
              history.push(`/property/${config.uuid}`);
              return;
            }
            //  tour already finalized
            //  in this case we want to continue with the flow
            if (tourAlreadyFinalized) {
              await dispatch({
                type: VerificationReducerActions.finalize_tour_success,
              });
            }
            history.push(`/tour/${tourToken}/schedule`);
          });
      }
    },
    [setToast, config.uuid, dispatch, history]
  );

  const processLicense = useCallback(
    ({ frontImage, backImage }: { frontImage: string; backImage: string }) => {
      return authenticatedRequest({
        url: `/identity-verification/front-and-back`,
        method: "POST",
        accessToken,
        data: {
          front_image: frontImage.split(",")[1],
          back_image: backImage.split(",")[1],
          group_uuid: groupTourConfigUuid,
          tour_token: tour?.token,
          document_type: "license",
        },
      });
    },
    [accessToken, groupTourConfigUuid, tour?.token]
  );

  const processPassport = useCallback(
    ({ image }: { image: string }) => {
      return authenticatedRequest({
        url: `/identity-verification/front`,
        method: "POST",
        accessToken,
        data: {
          image: image.split(",")[1],
          group_uuid: groupTourConfigUuid,
          tour_token: tour?.token,
          document_type: "passport",
        },
      });
    },
    [accessToken, groupTourConfigUuid, tour?.token]
  );

  const identityImageCheck = useCallback(
    ({
      frontImage,
      backImage,
      selfieImage,
    }: {
      frontImage?: string;
      backImage?: string;
      selfieImage?: string;
    }) => {
      const getSide = (
        frontImage: string | undefined,
        backImage: string | undefined,
        selfieImage: string | undefined
      ) => {
        if (frontImage && backImage) {
          return "front-and-back";
        }
        if (selfieImage) {
          return "selfie";
        }
        return "front";
      };
      const side = getSide(frontImage, backImage, selfieImage);

      return authenticatedRequest({
        url: `/identity-verification/${side}`,
        method: "POST",
        accessToken,
        data: {
          front_image:
            side === "front-and-back" && frontImage
              ? frontImage.split(",")[1]
              : null,
          back_image: backImage ? backImage.split(",")[1] : null,
          image:
            side === "selfie" && selfieImage
              ? selfieImage.split(",")[1]
              : side === "front" && frontImage
                ? frontImage.split(",")[1]
                : null,

          group_uuid: groupTourConfigUuid,
          tour_token: tour?.token,
        },
      });
    },
    [accessToken, groupTourConfigUuid, tour?.token]
  );

  const onTakeSelfie = useCallback(
    ({ selfieImage }: { selfieImage: string }) => {
      dispatch({ type: VerificationReducerActions.start_image_upload });

      identityImageCheck({ selfieImage })
        .catch((error) => {
          if (!state.selfieHasFailure) {
            dispatch({
              type: VerificationReducerActions.selfie_upload_failed,
              payload: error,
            });
          }
        })
        .finally(() => {
          setAppState((state: any) => ({ ...state, tour: undefined }));
          checkVerification({
            groupTourConfigUuid,
            verificationId,
            accessToken,
          });
        });
    },
    [
      dispatch,
      identityImageCheck,
      state.selfieHasFailure,
      setAppState,
      checkVerification,
      groupTourConfigUuid,
      verificationId,
      accessToken,
    ]
  );

  const setError = useCallback(
    ({ error }: { error: string | null }) => {
      dispatch({ type: VerificationReducerActions.set_error, payload: error });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!tour?.requireIdVerification && !state.creatingTour) {
      finalizeTour({
        tourToken: tour?.token || "",
        accessToken: accessToken,
      });
    }

    if (!state.creatingTour) {
      checkVerification({
        groupTourConfigUuid,
        verificationId,
        accessToken,
      });
    }
  }, [
    accessToken,
    checkVerification,
    finalizeTour,
    groupTourConfigUuid,
    state.alreadyVerified,
    state.creatingTour,
    tour?.requireIdVerification,
    tour?.token,
    verificationId,
  ]);

  const triggerCheckVerification = useCallback(
    (start_image_upload = true) => {
      if (start_image_upload)
        dispatch({ type: VerificationReducerActions.start_image_upload });

      checkVerification({
        groupTourConfigUuid,
        verificationId,
        accessToken,
      });
    },
    [
      accessToken,
      checkVerification,
      dispatch,
      groupTourConfigUuid,
      verificationId,
    ]
  );

  useEffect(() => {
    if (state.alreadyVerified) {
      finalizeTour({ tourToken: tour?.token ?? "", accessToken });
    }
  }, [accessToken, finalizeTour, state.alreadyVerified, tour?.token]);

  return {
    checkingVerification: state.checkingVerification,
    alreadyVerified: state.alreadyVerified,
    needsManualVerification: state.needsManualVerification,
    documentUploadComplete: state.documentUploadComplete,
    verificationComplete: state.verificationComplete,
    processing: state.processing,
    creatingTour: state.creatingTour,
    error: state.error,
    frontAttempts: state.frontAttempts,
    backAttempts: state.backAttempts,
    selfieAttempts: state.selfieAttempts,
    triggerCheckVerification,
    processLicense,
    processPassport,
    setError,
    onTakeSelfie,
  };
}

export { VerificationProvider };
